/**
 * Class to manage in-memory attachments for agent session events,
 * allowing attachments to be associated with specific events
 * and retrieved later when hydrating sessions or handling events.
 */
export class AgentSessionEventAttachments {
    constructor(sdk, agent) {
        this.sdk = sdk;
        this.agent = agent;
        this.attachmentMaps = new Map();
    }
    /**
     * Retrieve in-memory attachments for a given session ID and event ID.
     */
    get(sessionId, eventId) {
        return this.attachmentMaps.get(sessionId)?.get(eventId);
    }
    /**
     * Set in-memory attachments for a given session ID and event ID.
     * This is used to associate attachments with specific agent session events,
     * allowing them to be retrieved later when hydrating sessions or handling events.
     */
    set(sessionId, eventId, attachments) {
        const sessionMap = this.attachmentMaps.get(sessionId) || new Map();
        sessionMap.set(eventId, attachments);
        return this.attachmentMaps.set(sessionId, sessionMap);
    }
    /**
     * Delete in-memory attachments for a given session ID and event ID.
     */
    delete(sessionId, eventId) {
        return this.attachmentMaps.get(sessionId)?.delete(eventId) || false;
    }
    /**
     * Clear in-memory attachments for a given session ID,
     * or for all sessions if no session ID is provided.
     * @param sessionId if provided, clears attachments for the specified session; if blank, clears all in-memory attachments.
     */
    clear(sessionId) {
        if (sessionId !== undefined) {
            this.attachmentMaps.delete(sessionId);
        }
        else {
            this.attachmentMaps.clear();
        }
    }
    /**
     * Set in-memory attachments for the most recent user message event in the session that matches the provided message content.
     * This is a convenience method to associate attachments with the latest user message event,
     * which is a common use case when uploading attachments in response to a user message.
     * @param message the content of the user message to match when finding the most recent event to associate attachments with
     * @param session the agent session to search for the most recent user message event
     * @param attachments the attachments to associate with the most recent user message event that matches the provided message content
     * If a matching event is found, the attachments will be set in-memory for that event ID, allowing them to be retrieved later when hydrating sessions or handling events.
     */
    setOnLastUserMessage(message, session, attachments) {
        if (!attachments?.length) {
            return;
        }
        const event = this.sdk.ai.agents[this.agent].events.latestWithMessage(session, message, {
            role: "user"
        });
        if (event) {
            this.set(session.id, event?.id, attachments);
        }
    }
    /**
     * Hydrate an agent session's events with their associated attachments from in-memory storage.
     * This is used to enrich agent session events with their attachments when retrieving sessions or handling events,
     * allowing the attachments to be easily accessed alongside the event data.
     */
    hydrate(session) {
        if (!session.events?.length) {
            return session;
        }
        const sessionAttachments = this.attachmentMaps.get(session.id);
        if (!sessionAttachments) {
            return session;
        }
        for (const event of session.events) {
            const attachments = sessionAttachments.get(event.id);
            if (attachments) {
                event.attachments = attachments;
            }
        }
        return session;
    }
}
//# sourceMappingURL=sessions.events.attachments.js.map